import { z } from 'zod';
import { PaymentMethodType } from '@/models/common/PaymentMethodType';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import { isHebrewName } from '@/helpers/hebrewName';

const HEBREW_NAME_ONLY = 'יש לכתוב את השם באותיות עבריות בלבד, ללא סימנים';
import { isBirthDateInAllowedRange, MIN_PLAUSIBLE_YEAR } from '@/helpers/dateHelper';

// Address Schema
const addressSchema = z.object({
  streetName: z.string().optional().default(''),
  streetNumber: z.string().optional().default(''),
  apartmentNumber: z.string().optional().default(''),
  city: z.string().min(2, 'שם עיר חובה'),
  country: z.string().optional().default(''),
  zipCode: z.string().optional().default(''),
  pob: z.string().optional().default(''),
  updatedAt: z.date().optional(),
}).optional();

// Contact Schema - matches ContactDtoOut
const contactSchema = z.object({
  id: z.number().default(0),
  israeliId: z.string()
    .length(9, 'ת.ז. חייב להיות 9 ספרות')
    .regex(/^\d+$/, 'ת.ז. חייב להכיל ספרות בלבד')
    .refine(isValidIsraeliID, 'ת.ז. לא תקין'),
  firstName: z.string().min(2, 'שם פרטי חייב להיות לפחות 2 תווים').refine(isHebrewName, HEBREW_NAME_ONLY),
  lastName: z.string().min(2, 'שם משפחה חייב להיות לפחות 2 תווים').refine(isHebrewName, HEBREW_NAME_ONLY),
  email: z.string().email('כתובת אימייל לא תקינה').optional().or(z.literal('')),
  phone: z.string()
    .regex(/^05\d{8}$/, 'מספר טלפון חייב להתחיל ב-05 ולהכיל 10 ספרות')
    .optional().or(z.literal('')),
  address: addressSchema,
  birthDate: z.date().optional(),
});

// Spouse Schema - matches SpouseDtoOut
const spouseSchema = z.object({
  id: z.number().default(0),
  firstName: z.string().min(2, 'שם פרטי חייב להיות לפחות 2 תווים').refine(isHebrewName, HEBREW_NAME_ONLY),
  israeliId: z.string()
    .length(9, 'ת.ז. חייב להיות 9 ספרות')
    .regex(/^\d+$/, 'ת.ז. חייב להכיל ספרות בלבד')
    .refine(isValidIsraeliID, 'ת.ז. לא תקין'),
});

// Child Schema - matches ChildDtoOut
const childSchema = z.object({
  id: z.number().default(0),
  firstName: z.string().min(2, 'שם פרטי חייב להיות לפחות 2 תווים').refine(isHebrewName, HEBREW_NAME_ONLY),
  israeliId: z.string()
    .length(9, 'ת.ז. חייב להיות 9 ספרות')
    .regex(/^\d+$/, 'ת.ז. חייב להכיל ספרות בלבד')
    .refine(isValidIsraeliID, 'ת.ז. לא תקין'),
  // Kept as an optional Date so the field can start empty, but a child cannot
  // be saved without it
  birthDate: z.date().optional()
    .refine((val) => !!val, "תאריך לידה חובה")
    .refine(isBirthDateInAllowedRange, `תאריך לידה לא יכול להיות לפני ${MIN_PLAUSIBLE_YEAR}`),
  livesWithSupported: z.boolean().default(true),
});

// Bank Account Schema - matches BankAccountDetailsDtoOut
const bankAccountSchema = z.object({
  id: z.number().default(0),
  type: z.enum(PaymentMethodType).default(PaymentMethodType.BankAccount),
  bankNumber: z.number().min(1, 'מספר בנק חובה'),
  branchNumber: z.string().min(1, 'מספר סניף חובה'),
  accountNumber: z.string().min(1, 'מספר חשבון חובה'),
  accountOwnerName: z.string().min(2, 'שם בעל החשבון חובה'),
  documents: z.array(z.any()).default([]),
});

export const createDefaultBankAccount = () => ({
  id: 0,
  type: PaymentMethodType.BankAccount,
  bankNumber: 0,
  branchNumber: '',
  accountNumber: '',
  accountOwnerName: '',
  documents: [],
});

// Required Document Schema - for documents defined by classification
const requiredDocumentSchema = z.object({
  file: z.instanceof(File),
  requiredDocName: z.string(),
});

// Additional Document Schema - for custom documents added by user
const additionalDocumentSchema = z.object({
  file: z.instanceof(File).nullable(),
  customDescription: z.string(),
});

// Contact Classification Schema - matches ContactClassificationDtoOut
const contactClassificationSchema = z.object({
  id: z.number().default(0),
  classificationId: z.number(),
  requiredDocuments: z.array(requiredDocumentSchema).default([]),
  additionalDocuments: z.array(additionalDocumentSchema).default([]),
  comments: z.string().optional().default(''),
});

// Children Documents Schema - shared documents for all children (ID verification, etc.)
const childrenDocumentSchema = z.instanceof(File);

/*
 * HAT-1464: what a deleted supported already has, when their form is filled in again. Shown, not
 * edited: a child is a contact of their own and a bank account is what payments were made to, so
 * either is kept or dropped - and a wrong one is dropped and entered again as a new one.
 */
const existingChildSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  israeliId: z.string(),
  birthDate: z.string().nullable(),
  status: z.string(),
});

const existingBankAccountSchema = z.object({
  id: z.number(),
  bankNumber: z.number(),
  bankName: z.string().nullable(),
  branchNumber: z.string(),
  accountNumber: z.string(),
  accountOwnerName: z.string(),
  status: z.string(),
});

export type ExistingChild = z.infer<typeof existingChildSchema>;
export type ExistingBankAccount = z.infer<typeof existingBankAccountSchema>;

export const MIN_ONE_BANK_ACCOUNT = 'חובה להזין לפחות חשבון בנק אחד';

// Main Form Schema - matches SupportedFullInfoDtoOut
export const supportedFormSchema = z.object({
  contact: contactSchema,
  spouse: spouseSchema.nullable().default(null),
  children: z.array(childSchema).default([]),
  childrenDocuments: z.array(childrenDocumentSchema).default([]),
  existingChildren: z.array(existingChildSchema).default([]),
  // "At least one account" counts the kept existing ones too, so it is checked by the bank step
  // (NewSupportedForm) and not here: a re-entered supported may keep their account and add none.
  paymentInfos: z.array(bankAccountSchema).default([createDefaultBankAccount()]),
  existingPaymentInfos: z.array(existingBankAccountSchema).default([]),
  contactClassifications: z.array(contactClassificationSchema).default([]),
  declarationsComplete: z.boolean().refine(val => val === true, { message: 'יש לסמן את כל ההצהרות ולמלא את שם המצהיר' }),
});

// Export TypeScript type
export type SupportedFormData = z.infer<typeof supportedFormSchema>;

// Default values for the form
export const defaultFormValues: SupportedFormData = {
  contact: {
    id: 0,
    israeliId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: {
      streetName: '',
      streetNumber: '',
      apartmentNumber: '',
      city: '',
      country: '',
      zipCode: '',
      pob: '',
      updatedAt: undefined,
    },
    birthDate: undefined,
  },
  spouse: null,
  children: [],
  childrenDocuments: [],
  existingChildren: [],
  paymentInfos: [createDefaultBankAccount()],
  existingPaymentInfos: [],
  contactClassifications: [],
  declarationsComplete: false,
};

